// © Microsoft Corporation. All rights reserved.

import {
    ITrackInfo,
    KeyValueArray,
    KeyValuePair,
    Utility,
    getAppEnvironment,
    getHeadData,
    oneServiceQueryStringKeys
} from "@msnews/core";
import { aadTokenQsKey, aadTokenQsVal, resolveAadToken, useEnterpriseCompliance } from "@msnews/auth";

import { isString } from "lodash-es";

/**
 * A namespace that provides OneService utility functionality
 */
export namespace OneServiceUtility {

    /**
     * Authentication ticket type - Header value required by back-end service
     * Specifies what type of authentication to do
     */
    export const authenticationTicketType = "10";

    /**
     * Header name that holds which ANON cookie to use
     */
    export const signedInCookieNameHeader = "signedInCookieName";

    /**
     * Header name that holds the user-location value.
     */
    export const userLocationHeader = "user-location";

    /**
     * The api-key we'll be sending to OneService.
     */
    export const apiKey = "0QfOX3Vn51YCzitbLaRkTTBadtWpgTN8NZLW0C1SEM";

    /**
     * Executes a callback on completing of a service request
     *
     * @param {() => Promise<T>} serviceRequest - The service request
     * @param {() => void} callbackMethod - The callback method
     * @return {Promise<Response>} - A promise of the service response
     */
    export async function serviceRequestOnComplete<T>(serviceRequest: () => Promise<T>, callbackMethod: () => void): Promise<T> {
        try {
            const result = await serviceRequest();
            callbackMethod();

            return result;
        } catch (error) {
            callbackMethod();
            throw error;
        }
    }

    /**
     * Gets common parameters used in service requests to OneService, when there is no personalization involved
     * @param {string} userId - The user id for the request
     * @param {string} ocidSpecifier - The OCID
     * @param {string} addPageInfoToOcid - If true, page_product and page_type are added to the ocid. Defaults to true
     * @returns {KeyValueArray<string, string>}
     */
    export function getOneServiceParamsWithoutAuth(userId: string = null, ocidSpecifier: string, addPageInfoToOcid: boolean = true): KeyValueArray<string, string> {
        const appEnvironment = getAppEnvironment();
        const params = [
            {
                key: oneServiceQueryStringKeys.apiKey,
                value: apiKey
            },
            {
                // TODO Bug 22266910: clarify use of activityId vs rid in this spot
                key: oneServiceQueryStringKeys.activityId,
                value: appEnvironment.ActivityId
            },
            {
                key: oneServiceQueryStringKeys.ocid,
                value: addPageInfoToOcid ? getOneServiceOCID(appEnvironment.TrackInfo, ocidSpecifier) : ocidSpecifier
            },
            {
                key: oneServiceQueryStringKeys.market,
                value: appEnvironment.CurrentMarket
            }
        ];

        // 21527208 - remove this after upgrading experiences to use shared lib.
        if (userId) {
            params.push({
                key: oneServiceQueryStringKeys.user,
                value: userId
            });
        }

        let runningExperiments = getHeadData().CurrentFlights;
        if (runningExperiments) {
            params.push({
                key: oneServiceQueryStringKeys.fdhead,
                value: runningExperiments
            });
        }

        if (useEnterpriseCompliance()) {
            params.push({
                key: aadTokenQsKey,
                value: aadTokenQsVal
            });
        }

        return params;
    }

    /**
     * Gets common parameters used in service requests to OneService,
     * plus authentication params in the form of the sign in cookie name
     * Note: This should only be called if the user is known to be signed in, else use getOneServiceParamsWithoutAuth
     *
     * @export
     * @param {string} userId - The user id for the request
     * @param {string} ocidSpecifier - The OCID
     * @param {string} addPageInfoToOcid - If true, page_product and page_type are added to the ocid. Defaults to true
     * @returns {KeyValueArray<string, string>}
     */
    export function getOneServiceParamsWithAuth(userId: string = null, ocidSpecifier: string, addPageInfoToOcid: boolean = true): KeyValueArray<string, string> {
        const appEnvironment = getAppEnvironment();
        const authCookieName: string = appEnvironment.AuthCookieName as string;
        let params = [...getOneServiceParamsWithoutAuth(userId, ocidSpecifier, addPageInfoToOcid)];
        if (authCookieName) {
            params.push({
                key: oneServiceQueryStringKeys.signInCookieName, // sign-in cookie name
                value: authCookieName.toUpperCase()
            });
        }

        return params;
    }

    /**
     * Gets common headers used in service requests to OneService
     *
     * @export
     * @returns {Promise<HeadersInit>}
     */
    export async function getOneServiceHeaders(): Promise<HeadersInit> {
        const headers = {};

        if (useEnterpriseCompliance()) {
            const aadToken = await resolveAadToken();
            if (aadToken) {
                headers["Authorization"] = `Bearer ${aadToken}`;
            }
        }

        return headers;
    }

    /**
     * Gets the location query string parameters
     */
    export function getLocationParams(): KeyValuePair<string, string> {
        const appEnvironment = getAppEnvironment();
        const latitude = appEnvironment.Latitude;
        const longitude = appEnvironment.Longitude;

        if (latitude && longitude) {
            return { key: oneServiceQueryStringKeys.location, value: `${latitude}|${longitude}` };
        }

        return null;
    }

    /**
     * Returns the OCID generated from the telemetry TrackInfo if it is valid, otherwise returns the ocid specifier
     * @param {ITrackInfo} trackInfo - The track info
     * @param {ocidSpecifier} string - The OCID
     * @returns {string}
     */
    export function getOneServiceOCID(trackInfo: ITrackInfo, ocidSpecifier: string): string {
        if (Utility.isNonNullObject(trackInfo) &&
            Utility.isNonNullObject(trackInfo.sitePage) &&
            isString(trackInfo.sitePage.page_product) &&
            isString(trackInfo.sitePage.page_type)) {
            return trackInfo.sitePage.page_product + "-" + trackInfo.sitePage.page_type + (ocidSpecifier ? "-" + ocidSpecifier : "");
        } else {
            return ocidSpecifier;
        }
    }

    /**
     * Sets up base request data for One Service calls
     * @param method
     * @returns - RequestInit
     */
    export async function getBaseRequestData(method): Promise<RequestInit> {
        const requestObj: RequestInit = {
            headers: await OneServiceUtility.getOneServiceHeaders(),
            method
        };

        return requestObj;
    }

    /**
     * Populates the request init and return commonParams
     * @param ocid -- the ocid for this request
     * @returns {KeyValueArray<string, string>} - parameters as a key/value array
     */
    export function getCommonParams(ocid: string): KeyValueArray<string, string> {
        if (getHeadData().UserIsSignedIn) {
            return this.getOneServiceParamsWithAuth(getAppEnvironment().UserId, ocid);
        }

        return this.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, ocid);
    }
}